import { Alert, AlertTitle, Link } from '@customink/pigment-react';
import type {
  DecoratedDesignResponse,
  Product,
} from '@customink/project-service-react-components';
import { useEffect } from 'react';

import { googleAnalyticsTag } from '../vendors/googleAnalytics';

import { useOpenCatalogIframe } from '@/components/CatalogIframe';
import { useEditDesignLink } from '@/hooks/Design';
import { useOrderQuantityType } from '@/hooks/useOrderQuantityType';
import { biggestColorCount } from '@/utils/design';
import { pageViewTag } from '@/utils/metrics';

type TooManyColorsErrorProps = {
  design: DecoratedDesignResponse;
  product?: Product;
};

export default function TooManyColorsError({
  design,
  product,
}: TooManyColorsErrorProps) {
  const openCatalogIframe = useOpenCatalogIframe();
  const editDesign = useEditDesignLink(design?.compositeId);
  const quantityType = useOrderQuantityType();
  const totalColors = biggestColorCount(design);
  useEffect(() => {
    pageViewTag(`checkout/summary/${quantityType}/wcibmo/too_many_colors`);
  }, [quantityType]);

  const handleEditDesign = () => {
    googleAnalyticsTag({
      event: 'customEvent',
      eventAction: 'too many colors click',
      eventLabel: 'reduce colors',
      eventCategory: 'wcibmo',
    });
    editDesign();
  };

  const handleChooseProduct = () => {
    googleAnalyticsTag({
      event: 'customEvent',
      eventCategory: 'wcibmo',
      eventAction: 'too many colors click',
      eventLabel: 'choose different product',
    });
    openCatalogIframe({
      designCid: design?.compositeId,
      filters: { color_limit: totalColors },
    });
  };

  if (!product?.color_limit) return null;

  return (
    <Alert data-testid="too-many-colors-error" severity="error">
      <AlertTitle>Too many colors in design</AlertTitle>
      This product can only take {product.color_limit} ink color
      {product.color_limit > 1 && 's'} to ensure the best print.{' '}
      <Link onClick={handleEditDesign}>Reduce the colors in your design</Link>{' '}
      or <Link onClick={handleChooseProduct}>choose a different product</Link>.
    </Alert>
  );
}
